import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { ChevronDown } from "lucide-react";
import type { EkIcerik as EkIcerikVerisi, IcerikTablosu } from "@/lib/icerik_tipleri";
import { Figur, YolHaritasi, SoruKartlari, MaddeGorseli, gorselTuruSec, GORSEL_ETIKET } from "@/components/IcerikGorsel";
import { gorselYolu, type SayfaGorsel } from "@/lib/sayfa_gorsel_ortak";
// SayfaGorsel.tsx ve sayfa_gorsel_ortak.ts (~0,6 KB gzip, veri yok) bu içe aktarmayla ana pakete girer; şablon rotaları
// görsel için ayrı bir parça istemez (ayrı parça Lighthouse mobilde LCP'yi geciktiriyordu, 4 Kasım ölçümü).
import { AraGorsel } from "@/components/SayfaGorsel";
import { EK_BOLUM_GORSELLI_YOLLAR } from "@/lib/sayfa_gorsel_veri/ek_bolum_yollari";

/** En fazla bu kadar bölüm için madde çizimi üretilir (yol haritası ve soru kartları hariç). */
const MAKS_MADDE_GORSELI = 3;

/**
 * Ek içerik bölümleri: detaylı rehber + vektörel çizimler + ek SSS (sayfanın altında).
 * `gorseller`: bölüm başlığı → sayfa görseli (lib/sayfa_gorsel_veri/ek_bolum.ts `bolum` alanı); bölümün paragraflarından
 * sonra basılır.
 */
export function EkIcerikBolumleri({ ek, gorseller }: { ek: EkIcerikVerisi; gorseller?: Record<string, SayfaGorsel[]> }) {
  const [acik, setAcik] = useState<number | null>(null);
  // Çizim planı: 01 yol haritası, sonra maddeli bölümler sırayla (adım → kart → hub → huni)
  let gorselNo = 1;
  let maddeSayac = 0;
  const bolumGorsel = ek.bolumler.map(b => {
    if (!b.maddeler || b.maddeler.length < 3 || maddeSayac >= MAKS_MADDE_GORSELI) return null;
    const tur = gorselTuruSec(maddeSayac, b.maddeler.length);
    maddeSayac += 1;
    gorselNo += 1;
    return { tur, no: gorselNo };
  });
  const toplamGorsel = 1 + maddeSayac;
  const soruKartlari = toplamGorsel < 3 && ek.sss && ek.sss.length >= 3;
  return (
    <section className="py-14 md:py-20 px-4 md:px-8 bg-white border-t-3 border-black" aria-label="Detaylı rehber">
      <div className="max-w-4xl mx-auto space-y-12">
        {ek.bolumler.length >= 2 && (
          <Figur no={1} etiket="Bu rehberin yol haritası">
            <YolHaritasi basliklar={ek.bolumler.slice(0, 6).map(b => b.baslik)} />
          </Figur>
        )}
        <div className="space-y-10">
          {ek.bolumler.map((b, i) => {
            const g = bolumGorsel[i];
            return (
              <div key={b.baslik} className="space-y-4">
                <h2 className="font-black text-2xl md:text-3xl tracking-tight leading-tight">{b.baslik}</h2>
                {b.paragraflar.map((pr, j) => (
                  <p key={j} className="text-sm md:text-base text-neutral-700 font-semibold leading-relaxed">{pr}</p>
                ))}
                {gorseller?.[b.baslik]?.map(gs => <AraGorsel key={gs.src} g={gs} className="" />)}
                {b.altBolumler?.map(ab => (
                  <div key={ab.baslik} className="space-y-3 pt-2">
                    <h3 className="font-black text-lg md:text-xl tracking-tight leading-snug">{ab.baslik}</h3>
                    {ab.paragraflar.map((pr, j) => (
                      <p key={j} className="text-sm md:text-base text-neutral-700 font-semibold leading-relaxed">{pr}</p>
                    ))}
                    {ab.baglantilar && ab.baglantilar.length > 0 && <Baglantilar liste={ab.baglantilar} />}
                  </div>
                ))}
                {b.tablo && <KarsilastirmaTablosu tablo={b.tablo} />}
                {b.maddeler && b.maddeler.length > 0 && g && (
                  <>
                    <Figur no={g.no} etiket={GORSEL_ETIKET[g.tur]}>
                      <MaddeGorseli tur={g.tur} maddeler={b.maddeler} merkez={b.baslik} />
                    </Figur>
                    <ul className="sr-only">
                      {b.maddeler.map(m => <li key={m}>{m}</li>)}
                    </ul>
                  </>
                )}
                {b.maddeler && b.maddeler.length > 0 && !g && (
                  <ul className="space-y-2 pl-1">
                    {b.maddeler.map(m => (
                      <li key={m} className="flex gap-3 text-sm md:text-base text-neutral-800 font-semibold leading-relaxed">
                        <span className="mt-2 w-2 h-2 shrink-0 bg-[#9EFF00] border border-black" aria-hidden="true" />
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {b.baglantilar && b.baglantilar.length > 0 && <Baglantilar liste={b.baglantilar} />}
              </div>
            );
          })}
        </div>
        {ek.sss && ek.sss.length > 0 && (
          <div className="space-y-4">
            <h2 className="font-black text-2xl md:text-3xl tracking-tight">Sık Sorulan Diğer Sorular</h2>
            {soruKartlari && (
              <Figur no={toplamGorsel + 1} etiket="Soru haritası">
                <SoruKartlari sorular={ek.sss.map(s => s.q)} />
              </Figur>
            )}
            {ek.sss.map((f, i) => (
              <div key={f.q} className="border-3 border-black bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <button type="button" onClick={() => setAcik(acik === i ? null : i)} aria-expanded={acik === i} className="w-full flex justify-between items-center gap-4 p-5 text-left font-black text-sm md:text-base">
                  <span>{f.q}</span>
                  <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${acik === i ? "rotate-180" : ""}`} />
                </button>
                <div hidden={acik !== i} className="p-5 border-t-3 border-black text-xs md:text-sm text-neutral-600 font-bold leading-relaxed">{f.a}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Baglantilar({ liste }: { liste: { href: string; etiket: string }[] }) {
  return (
    <div className="flex flex-wrap gap-3 pt-1">
      {liste.map(l => (
        <Link key={l.href} href={l.href} className="border-2 border-black bg-white px-3 py-1.5 text-xs font-bold hover:bg-[#9EFF00] transition-colors">
          {l.etiket} →
        </Link>
      ))}
    </div>
  );
}

/** Karşılaştırma tablosu; SSR karşılığı server/seo/render.ts tabloHtml (aynı başlık, satır ve caption). */
function KarsilastirmaTablosu({ tablo }: { tablo: IcerikTablosu }) {
  return (
    <div className="overflow-x-auto border-3 border-black">
      <table className="w-full min-w-[720px] border-collapse text-left text-xs md:text-sm">
        <caption className="sr-only">{tablo.aciklama}</caption>
        <thead>
          <tr className="bg-black text-[#9EFF00]">
            {tablo.basliklar.map(h => (
              <th key={h} scope="col" className="p-3 font-black align-bottom">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tablo.satirlar.map(s => (
            <tr key={s[0]} className="border-t-2 border-black even:bg-neutral-50">
              {s.map((h, j) =>
                j === 0 ? (
                  <th key={j} scope="row" className="p-3 font-black align-top whitespace-nowrap">{h}</th>
                ) : (
                  <td key={j} className="p-3 font-semibold text-neutral-700 leading-relaxed align-top">{h}</td>
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Ek içerik görseli olan yol mu (sorgu, çapa ve sondaki "/" atılır); öyleyse temiz yol. */
function ekGorselliYol(yol: string): string | null {
  const temiz = gorselYolu(yol);
  return EK_BOLUM_GORSELLI_YOLLAR.includes(temiz) ? temiz : null;
}

/** Geçerli yola göre ek içeriği otomatik basar (Layout içinde, footer'dan önce). */
export default function EkIcerikOto() {
  const [location] = useLocation();
  const [veri, setVeri] = useState<{ ek: EkIcerikVerisi; gorseller: Record<string, SayfaGorsel[]> } | null>(null);
  // İçerik tablosu (~600 KB metin) ana pakete girmesin diye tembel yüklenir; SSR aynı veriyi sunucuda basar. Bölüme bağlı
  // görsel modülü (lib/sayfa_gorsel_veri/ek_bolum.ts) yalnız listedeki yollarda indirilir; diğer sayfalarda ek istek yok.
  useEffect(() => {
    let aktif = true;
    const ekYol = ekGorselliYol(location);
    // Görsel modülü yüklenemezse ek içerik görselsiz basılır.
    const gorselYukle = ekYol
      ? import("@/lib/sayfa_gorsel_veri/ek_bolum").then(
          g => g.ekBolumGorselleri(ekYol),
          () => ({})
        )
      : Promise.resolve({});
    Promise.all([import("@/lib/ek_icerik"), gorselYukle]).then(([m, gorseller]) => {
      if (!aktif) return;
      const ek = m.ekIcerikBul(location);
      setVeri(ek ? { ek, gorseller } : null);
    });
    return () => {
      aktif = false;
    };
  }, [location]);
  if (!veri) return null;
  return <EkIcerikBolumleri key={location} ek={veri.ek} gorseller={veri.gorseller} />;
}
